/**
 * Shared rules for list-page query strings (/cars, /vehicles, category lists).
 *
 * - Tracking params (utm_*, gclid, fbclid…) never change the result set, so
 *   they never make a page "filtered"; canonical points to the clean URL.
 * - `?page=N` alone is plain pagination: self-canonical and indexable.
 * - Any other param is a filtered/sorted search: `noindex, follow`.
 */

export const TRACKING_PARAM = /^(utm_[a-z0-9_]+|gclid|fbclid|gbraid|wbraid|msclkid|ttclid|yclid|mc_cid|mc_eid|_ga|_gl|ref)$/i;

export function isTrackingParam(key: string): boolean {
  return TRACKING_PARAM.test(key);
}

/** Non-empty, non-tracking params as strings. */
export function meaningfulParams(search: Record<string, unknown> | URLSearchParams): Record<string, string> {
  const entries = search instanceof URLSearchParams ? Array.from(search.entries()) : Object.entries(search);
  const out: Record<string, string> = {};
  for (const [k, v] of entries) {
    if (isTrackingParam(k)) continue;
    if (v === undefined || v === null || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

/** Tracking params only, to carry campaign attribution across redirects. */
export function trackingParams(search: Record<string, unknown> | URLSearchParams): URLSearchParams {
  const entries = search instanceof URLSearchParams ? Array.from(search.entries()) : Object.entries(search);
  const out = new URLSearchParams();
  for (const [k, v] of entries) if (isTrackingParam(k) && v != null && v !== "") out.set(k, String(v));
  return out;
}

export type ListSearchKind =
  | { kind: "clean"; page: 1 }
  | { kind: "page"; page: number }
  | { kind: "page-one" } // ?page=1 → redirect to the clean URL
  | { kind: "invalid-page" } // ?page=abc / 0 / -3 → 404
  | { kind: "filtered"; page: number };

export function classifyListSearch(search: Record<string, unknown> | URLSearchParams): ListSearchKind {
  const params = meaningfulParams(search);
  const keys = Object.keys(params);
  const rawPage = params.page;
  let page = 1;
  if (rawPage !== undefined) {
    if (!/^\d+$/.test(rawPage) || Number(rawPage) < 1) return { kind: "invalid-page" };
    page = Number(rawPage);
  }
  const others = keys.filter((k) => k !== "page");
  if (others.length > 0) return { kind: "filtered", page };
  if (rawPage === undefined) return { kind: "clean", page: 1 };
  if (page === 1) return { kind: "page-one" };
  return { kind: "page", page };
}

type Head = { meta: Array<Record<string, string>>; links: Array<Record<string, string>> };

/** Self-canonical, indexable head for `?page=N` of an indexable list. */
export function paginatedHead(base: Head, canonicalPath: string, page: number): Head {
  const canonical = `https://roautomarket.ro${canonicalPath}?page=${page}`;
  const suffix = ` – pagina ${page}`;
  const titleKeys = new Set(["og:title", "twitter:title", "title"]);
  const meta = base.meta.map((m) => {
    if ("title" in m && Object.keys(m).length === 1) return { title: m.title.replace(/( \| RO Auto Market)?$/, `${suffix}$1`) };
    const key = m.property || m.name;
    if (key && titleKeys.has(key) && m.content) return { ...m, content: m.content.replace(/( \| RO Auto Market)?$/, `${suffix}$1`) };
    if (key === "og:url") return { ...m, content: canonical };
    return m;
  });
  const links = base.links.filter((l) => l.rel !== "canonical");
  links.push({ rel: "canonical", href: canonical });
  return { meta, links };
}

/** `noindex, follow` head for filtered/sorted searches (no canonical). */
export function filteredHead(base: Head): Head {
  return {
    meta: [
      ...base.meta.filter((m) => m.property !== "og:url" && m.name !== "robots"),
      { name: "robots", content: "noindex, follow" },
    ],
    links: [],
  };
}
